import type { ReactNode } from 'react';
import type { Theme, SxProps } from '@mui/material/styles';

import { mergeClasses } from 'minimal-shared/utils';

import { styled } from '@mui/material/styles';

import { getSlideSize } from '../utils';
import { carouselClasses } from '../classes';

import type { CarouselOptions } from '../types';

// ----------------------------------------------------------------------

/**
 * 한 장. 폭은 `options.slidesToShow` 가 정하고, 간격은 `options.slideSpacing` 이 준다.
 *
 * ⚠️ **필수 프롭이 없다** — `options` 없이도 `getSlideSize()` 의 기본값(1장)으로 돈다.
 *
 * ⚠️ `options.parallax` 가 참이면 안을 두 겹(`content` → `parallax`)으로 더 감싼다.
 * 시차 이동은 그 안쪽 요소의 `transform` 을 훅이 직접 만진다.
 */
export type CarouselSlideProps = {
  sx?: SxProps<Theme>;
  options?: CarouselOptions;
  children?: ReactNode;
  className?: string;
};

export function CarouselSlide({ sx, options, children, className, ...other }: CarouselSlideProps) {
  const slideSize = getSlideSize(options?.slidesToShow);

  return (
    <CarouselSlideRoot
      axis={options?.axis ?? 'x'}
      slideSpacing={options?.slideSpacing}
      className={mergeClasses([carouselClasses.slide.root, className])}
      sx={[{ flex: slideSize }, ...(Array.isArray(sx) ? sx : [sx])]}
      {...other}
    >
      {options?.parallax ? (
        <div className={carouselClasses.slide.content}>
          <div className={carouselClasses.slide.parallax}>{children}</div>
        </div>
      ) : (
        children
      )}
    </CarouselSlideRoot>
  );
}

// ----------------------------------------------------------------------

type CarouselSlideStyleProps = {
  axis?: 'x' | 'y';
  slideSpacing?: string;
};

const CarouselSlideRoot = styled('li', {
  shouldForwardProp: (prop: string) => !['axis', 'slideSpacing', 'sx'].includes(prop),
})<CarouselSlideStyleProps>(({ slideSpacing }) => ({
  display: 'block',
  position: 'relative',
  [`& .${carouselClasses.slide.content}`]: {
    overflow: 'hidden',
    position: 'relative',
    borderRadius: 'inherit',
  },
  variants: [
    { props: { axis: 'x' }, style: { minWidth: 0, paddingLeft: slideSpacing } },
    { props: { axis: 'y' }, style: { minHeight: 0, paddingTop: slideSpacing } },
  ],
}));
